import api from '@/services/api';

export const fetchChartDataFromServer = async (params) => {
  const {data} = await api().post('/data', params);
  return data.data;
};

export const getRemoved = (
  newParams,
  lastFetchedChartParameters,
) => {
  let removedChannel = null;
  let removedParameter = null;
  let removedIndicator = null;

  if ('channels' in lastFetchedChartParameters) {
    removedChannel = lastFetchedChartParameters.channels.find(
      (channel) => !newParams.channels.includes(channel)
    );
  }

  if ('parameters' in lastFetchedChartParameters) {
    removedParameter = lastFetchedChartParameters.parameters.find(
      (parameter) => !newParams.parameters.includes(parameter)
    );
  }

  if ('indicators' in lastFetchedChartParameters) {
    removedIndicator = lastFetchedChartParameters.indicators.find(
      (parameter) => !newParams.indicators.includes(parameter)
    );
  }

  return removedChannel || removedParameter || removedIndicator;
};

export const getAddedChannel = (
  newParams,
  lastFetchedChartParameters,
) => {
  if (!newParams.channels || !Array.isArray(newParams.channels)) {
    return null;
  }

  return newParams.channels.find(
    (channel) => !lastFetchedChartParameters.channels.includes(channel),
  );
};

export const getAddedParameter = (
  newParams,
  lastFetchedChartParameters,
) => {
  if (!newParams.parameters || !Array.isArray(newParams.parameters)) {
    return null;
  }

  return newParams.parameters.find(
    (parameter) => !lastFetchedChartParameters.parameters.includes(parameter),
  );
};

export const getAddedIndicator = (
  newParams,
  lastFetchedChartParameters,
) => {
  if (!newParams.indicators || !Array.isArray(newParams.indicators)) {
    return null;
  }

  return newParams.indicators.find(
    (parameter) => !lastFetchedChartParameters.indicators.includes(parameter),
  );
};

export const getDataAfterRemoving = (removedKey, lastFetchedChartParameters, lastFetchedChartData) => {
  const newData = _.cloneDeep(lastFetchedChartData);

  if (!lastFetchedChartParameters.compare || lastFetchedChartParameters.compare === 'no') {
    const removedChannelIndex = lastFetchedChartData.sources.findIndex((channel) => `${channel}` === `${removedKey}`);

    newData.sources.splice(removedChannelIndex, 1);
    newData.vectors.splice(removedChannelIndex, 1);
    newData.vectorsCodes.splice(removedChannelIndex, 1);
    newData.mus.splice(removedChannelIndex, 1);
    newData.categories.splice(removedChannelIndex, 1);
    newData.columns.splice(removedChannelIndex + 1, 1); // Data offset
    newData.values.forEach(
      (value) => value.splice(removedChannelIndex + 1, 1), // Date offset
    );
  } else {
    const removedChannelIndex = lastFetchedChartData[0].sources.findIndex((channel) => `${channel}` === `${removedKey}`);

    [0, 1].forEach((index) => {
      newData[index].sources.splice(removedChannelIndex, 1);
      newData[index].vectors.splice(removedChannelIndex, 1);
      newData[index].vectorsCodes.splice(removedChannelIndex, 1);
      newData[index].mus.splice(removedChannelIndex, 1);
      newData[index].categories.splice(removedChannelIndex, 1);
      newData[index].columns.splice(removedChannelIndex + 1, 1); // Data offset
      newData[index].values.forEach(
        (value) => value.splice(removedChannelIndex + 1, 1), // Date offset
      );
    });
  }

  return newData;
}

export const getDataAfterAdding = async (
  addedChannel,
  addedParameter,
  addedIndicator,
  lastFetchedChartParameters,
  lastFetchedChartData,
) => {
  const newParameters = _.cloneDeep(lastFetchedChartParameters);

  if (addedChannel) {
    newParameters.channels = [addedChannel];
    newParameters.parameters = [];
  }

  if (addedParameter) {
    newParameters.channels = [];
    newParameters.parameters = [addedParameter];
    newParameters.indicators = [];
  }

  if (addedIndicator) {
    newParameters.channels = [];
    newParameters.parameters = [];
    newParameters.indicators = [addedIndicator];
  }

  const fetchedData = await fetchChartDataFromServer(newParameters);
  const newData = _.cloneDeep(lastFetchedChartData);

  if (!lastFetchedChartParameters.compare || lastFetchedChartParameters.compare === 'no') {
    newData.sources.push(fetchedData.sources[0]);
    newData.vectors.push(fetchedData.vectors[0]);
    newData.vectorsCodes.push(fetchedData.vectorsCodes[0]);
    newData.mus.push(fetchedData.mus[0]);
    newData.categories.push(fetchedData.categories[0]);
    newData.columns.push(fetchedData.columns[1]); // Data offset
    newData.values.forEach(
      (value, index) => value.push(fetchedData.values[index][1]), // Date offset
    );
  } else {
    [0, 1].forEach((index) => {
      newData[index].sources.push(fetchedData[index].sources[0]);
      newData[index].vectors.push(fetchedData[index].vectors[0]);
      newData[index].vectorsCodes.push(fetchedData[index].vectorsCodes[0]);
      newData[index].mus.push(fetchedData[index].mus[0]);
      newData[index].categories.push(fetchedData[index].categories[0]);
      newData[index].columns.push(fetchedData[index].columns[1]); // Data offset
      newData[index].values.forEach(
        (value, valueIndex) => value.push(fetchedData[index].values[valueIndex][1]), // Date offset
      );
    });
  }

  return newData;
};

export const isNeedFetchAllData = (
  newParams,
  lastFetchedChartParameters,
  lastFetchedChartData,
) => {
  if(! lastFetchedChartData) {
    return true;
  }
  
  if (!('values' in lastFetchedChartData)) {
    return true;
  }

  if (!Array.isArray(lastFetchedChartData.values[0])) {
    return true;
  }

  if (lastFetchedChartData.values[0].length < 2 ) {
    return true;
  }

  // Change fetching mode (measures or indicators)
  if (lastFetchedChartParameters.mode !== newParams.mode) {
    return true;
  }

  return false;
};

export const fetchChartData = async (
  newParams,
  lastFetchedChartParameters,
  lastFetchedChartData,
) => {
  const isNeedFetchFromServer = isNeedFetchAllData(
    newParams,
    lastFetchedChartParameters,
    lastFetchedChartData
  );
  if (isNeedFetchFromServer) {
    return await fetchChartDataFromServer(newParams);
  }

  const removedKey = getRemoved(newParams, lastFetchedChartParameters);
  if (removedKey) {
    return getDataAfterRemoving(removedKey, lastFetchedChartParameters, lastFetchedChartData);
  }

  const addedChannel = getAddedChannel(newParams, lastFetchedChartParameters);
  const addedParameter = getAddedParameter(newParams, lastFetchedChartParameters);
  const addedIndicator = getAddedIndicator(newParams, lastFetchedChartParameters);
  if (addedChannel || addedParameter || addedIndicator) {
    return await getDataAfterAdding(
      addedChannel,
      addedParameter,
      addedIndicator,
      lastFetchedChartParameters,
      lastFetchedChartData,
    );
  }

  return await fetchChartDataFromServer(newParams);
};



// WEBPACK FOOTER //
// ./src/helpers/chart.js